import { useCallback, useEffect, useRef, useState } from "react";

export const useMenuScroll = (filteredCategoriesLength: number) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingProgrammatically = useRef(false);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const tabsContainerRef = useRef<HTMLElement | null>(null);

  const scrollTabIntoView = useCallback((index: number) => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const tab = container.querySelector(
      `[data-tab-index="${index}"]`
    ) as HTMLElement | null;
    const scroller = container.querySelector(".MuiTabs-scroller");
    if (!tab || !scroller) return;

    const cRect = scroller.getBoundingClientRect();
    const tRect = tab.getBoundingClientRect();
    if (tRect.left < cRect.left || tRect.right > cRect.right) {
      tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, []);

  const detectActiveSection = useCallback(() => {
    if (isScrollingProgrammatically.current) return;

    const scrollY = window.scrollY;
    const viewportH = window.innerHeight;
    const headerOffset = 200;

    let activeIndex = 0;
    let maxVisibility = 0;

    sectionRefs.current.forEach((el, index) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + scrollY;
      const bottom = top + rect.height;

      const visibleTop = Math.max(top, scrollY + headerOffset);
      const visibleBottom = Math.min(bottom, scrollY + viewportH);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const ratio = visibleHeight / rect.height;

      const topBias = rect.top <= headerOffset ? 1.2 : 1;
      const adjusted = ratio * topBias;

      if (adjusted > maxVisibility && ratio > 0.1) {
        maxVisibility = adjusted;
        activeIndex = index;
      }
    });

    setActiveTabIndex((prev) => {
      if (activeIndex !== prev) scrollTabIntoView(activeIndex);
      return activeIndex;
    });
  }, [scrollTabIntoView]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(detectActiveSection, 10);
  }, [detectActiveSection]);

  const scrollToSection = useCallback((index: number) => {
    const el = sectionRefs.current[index];
    if (!el) return;

    isScrollingProgrammatically.current = true;
    setActiveTabIndex(index);

    const targetPosition = el.offsetTop - 180;
    window.scrollTo({ top: Math.max(0, targetPosition), behavior: "smooth" });

    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 1000);
  }, []);

  // Keep refs array in sync with category count
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, filteredCategoriesLength);
  }, [filteredCategoriesLength]);

  // Scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleScroll]);

  return {
    activeTabIndex,
    setActiveTabIndex,
    sectionRefs,
    tabsContainerRef,
    scrollToSection,
    isScrollingProgrammatically,
  };
};